import METHOD from 'config/api/_methods';

type EndpointConfig = {
  url: string;
  method: string;
};

type PaginatedResponse<T> = {
  next?: string | null;
  results: T[];
};

type FetchPaginatedPrintDataParams = {
  endpointFunc: (params: Record<string, unknown>) => EndpointConfig;
  apiCall: (endpoint: EndpointConfig, config?: Record<string, unknown>) => Promise<{ data?: PaginatedResponse<any>; status?: number }>;
  requestConfig?: Record<string, unknown>;
  queryParams?: Record<string, unknown>;
  limit?: number;
};

const DEFAULT_PRINT_PAGE_SIZE = 30;

const assertSuccessfulResponse = <T>(response: { data?: PaginatedResponse<T>; status?: number }): PaginatedResponse<T> => {
  if (
    !response ||
    (response.status && (response.status < 200 || response.status >= 300)) ||
    !response.data ||
    !Array.isArray(response.data.results)
  ) {
    throw new Error('Unable to fetch complete print data.');
  }
  return response.data;
};

const fetchPaginatedPrintData = async <T>({
  endpointFunc,
  apiCall,
  requestConfig = {},
  queryParams = {},
  limit = DEFAULT_PRINT_PAGE_SIZE,
}: FetchPaginatedPrintDataParams): Promise<T[]> => {
  let offset = 0;
  let hasNextPage = true;
  const rows: T[] = [];

  while (hasNextPage) {
    const endpoint = endpointFunc({ limit, offset, ...queryParams });
    // Pages must be fetched sequentially so a failing later page prevents a partial print.
    // eslint-disable-next-line no-await-in-loop
    const response = await apiCall(endpoint, endpoint.method === METHOD.GET ? {} : requestConfig);
    const data = assertSuccessfulResponse<T>(response);
    rows.push(...data.results);
    hasNextPage = Boolean(data.next);
    offset += limit;
  }

  return rows;
};

export default fetchPaginatedPrintData;
